import React from 'react';
import './Skeleton.css';

export function Skeleton({ 
  type = 'text', 
  width, 
  height, 
  className = '',
  style = {}
}) {
  const baseClass = 'skeleton';
  const typeClass = type ? `skeleton-${type}` : '';
  
  const computedStyle = {
    backgroundColor: '#f0f2f5', // fallback gray if CSS not loaded yet
    ...style,
    ...(width && { width }),
    ...(height && { height }),
  };

  return (
    <div 
      className={`${baseClass} ${typeClass} ${className}`.trim()}
      style={Object.keys(computedStyle).length > 0 ? computedStyle : undefined}
      aria-hidden="true"
    />
  );
}

export function SkeletonText({ lines = 3, className = '' }) {
  return (
    <div className={`skeleton-text-block ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton 
          key={i} 
          type="text" 
          className={i === lines - 1 ? 'skeleton-text--short' : ''}
        />
      ))}
    </div>
  );
}

export function SkeletonEventCard() {
  return (
    <div className="skeleton-event-card" aria-label="Loading event...">
      <div className="skeleton-event-card__top-row">
        <div className="skeleton-event-card__badges">
          <Skeleton type="badge" className="skeleton-badge" />
          <Skeleton type="badge" className="skeleton-badge" />
        </div>
        <Skeleton type="button" width="32px" height="32px" style={{ borderRadius: '50%' }} />
      </div>
      
      <Skeleton className="skeleton-event-card__title" />
      <Skeleton className="skeleton-event-card__description" />
      
      <div className="skeleton-event-card__info-row">
        <Skeleton className="skeleton-event-card__cost" />
        <Skeleton className="skeleton-event-card__status" />
      </div>
      
      <div className="skeleton-event-card__footer">
        <Skeleton className="skeleton-event-card__footer-btn" />
        <Skeleton className="skeleton-event-card__footer-btn" />
      </div>
    </div>
  );
}

export function SkeletonGroupCard() {
  return (
    <div className="skeleton-group-card" aria-label="Loading group...">
      <div className="skeleton-group-card__header">
        <Skeleton className="skeleton-group-card__name" />
        <div className="skeleton-group-card__actions">
          <Skeleton className="skeleton-icon-btn" />
          <Skeleton className="skeleton-icon-btn" />
        </div>
      </div>
      
      <Skeleton className="skeleton-group-card__balance" />
      
      <div className="skeleton-group-card__footer">
        <Skeleton className="skeleton-group-card__btn" />
        <Skeleton className="skeleton-group-card__btn" />
        <Skeleton className="skeleton-group-card__btn" />
      </div>
    </div>
  );
}

export function SkeletonActivityList() {
  return (
    <div className="activity-list-page" aria-label="Loading events...">
      <div className="skeleton-page-header">
        <Skeleton className="skeleton-page-header__title" />
        <Skeleton className="skeleton-page-header__btn" />
      </div>
      <div className="activities-grid">
        <SkeletonEventCard />
        <SkeletonEventCard />
        <SkeletonEventCard />
      </div>
    </div>
  );
}

export function SkeletonGroupsList() {
  return (
    <div className="groups-container" aria-label="Loading groups...">
      <div className="page-header">
        <Skeleton type="title" width="120px" />
      </div>
      <div className="groups-list">
        <SkeletonGroupCard />
        <SkeletonGroupCard />
        <SkeletonGroupCard />
      </div>
    </div>
  );
}

export function SkeletonDetailPage() {
  return (
    <div className="skeleton-detail-page" aria-label="Loading...">
      <div className="skeleton-detail-page__header">
        <Skeleton type="title" width="60%" />
        <Skeleton type="text" className="skeleton-text--medium" />
      </div>
      
      <div className="skeleton-detail-page__section">
        <Skeleton className="skeleton-detail-page__section-title" />
        <SkeletonText lines={3} />
      </div>
      
      <div className="skeleton-detail-page__section">
        <Skeleton className="skeleton-detail-page__section-title" />
        <Skeleton type="card" height="120px" />
      </div>
    </div>
  );
}

export function SkeletonAppShell() {
  return (
    <div className="skeleton-app-shell" aria-label="Loading application...">
      <div className="skeleton-app-shell__header">
        <Skeleton type="title" width="180px" />
        <Skeleton type="button" />
      </div>
      <div className="skeleton-app-shell__content">
        <Skeleton type="card" height="200px" />
        <Skeleton type="card" height="200px" />
      </div>
    </div>
  );
}

export function SkeletonOfferCard() {
  return (
    <div className="skeleton-card" aria-label="Loading offer..." style={{ minHeight: '180px' }}>
      <Skeleton height="100px" style={{ marginBottom: '12px', borderRadius: '8px' }} />
      <Skeleton type="title" width="70%" />
      <Skeleton type="text" className="skeleton-text--medium" />
      <Skeleton type="text" className="skeleton-text--short" />
    </div>
  );
}

export function SkeletonOffersList() {
  return (
    <div className="offers-container" aria-label="Loading offers...">
      <div className="skeleton-page-header" style={{ marginBottom: '20px' }}>
        <Skeleton className="skeleton-page-header__title" />
        <Skeleton type="text" width="200px" style={{ margin: '0 auto' }} />
      </div>
      <div className="offers-list">
        <SkeletonOfferCard />
        <SkeletonOfferCard />
        <SkeletonOfferCard />
      </div>
    </div>
  );
}

export function SkeletonNotificationItem() {
  return (
    <div className="skeleton-card" style={{ padding: '16px' }}>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        <Skeleton type="avatar" className="skeleton-avatar--sm" />
        <div style={{ flex: 1 }}>
          <Skeleton type="text" className="skeleton-text--medium" />
          <Skeleton type="text" className="skeleton-text--short" />
        </div>
      </div>
    </div>
  );
}

export function SkeletonNotificationsList() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <SkeletonNotificationItem />
      <SkeletonNotificationItem />
      <SkeletonNotificationItem />
      <SkeletonNotificationItem />
      <SkeletonNotificationItem />
    </div>
  );
}

export default Skeleton;
